import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik, Field, type FieldProps } from "formik";
import * as Yup from "yup";
import { Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { useHeaderAction } from "../../../../core/provider/HeaderActionProvider/HeaderAction.js";
import HeaderSubPages from "../../../../components/ui/HeaderSubPages/index.js";
import { Input } from "../../../../components/ui/input/Input.js";
import { Button } from "../../../../components/ui/Button/index.js";
import { toastify } from "../../../../components/ui/Toasts/index.js";
import { useChangePassword } from "../../../../core/api/auth/mutation.js";

// ============================================================
// PasswordInput — دکمه چشم مستقل
// ============================================================
interface PasswordInputProps {
    field: any;
    meta: any;
    label: string;
    autoComplete?: string;
}

function PasswordInput({
    field,
    meta,
    label,
    autoComplete,
}: PasswordInputProps) {
    const [show, setShow] = useState(false);

    return (
        <div className="relative w-full">
            <Input
                {...field}
                variant="floating"
                label={label}
                rounded="xl"
                className="h-12"
                type={show ? "text" : "password"}
                autoComplete={autoComplete}
                rightIcon={<Lock className="w-4 h-4" />}
                error={meta.touched && !!meta.error}
                errorMessage={meta.touched ? meta.error : undefined}
            />

            <button
                type="button"
                onClick={() => setShow((p) => !p)}
                className="absolute top-1/2 -translate-y-1/2 left-3 z-10 flex items-center justify-center w-7 h-7 rounded-md hover:bg-neutral-100 transition-colors"
                tabIndex={-1}
                aria-label={show ? "مخفی کردن رمز" : "نمایش رمز"}
            >
                {show ? (
                    <EyeOff className="w-4 h-4 text-neutral-500" />
                ) : (
                    <Eye className="w-4 h-4 text-neutral-500" />
                )}
            </button>
        </div>
    );
}

// ============================================================
// اعتبارسنجی
// ============================================================
const validationSchema = Yup.object()
    .shape({
        currentPassword: Yup.string()
            .required("رمز عبور فعلی الزامی است")
            .min(6, "رمز عبور فعلی باید حداقل ۶ کاراکتر باشد"),
        newPassword: Yup.string()
            .required("رمز عبور جدید الزامی است")
            .min(6, "رمز عبور جدید باید حداقل ۶ کاراکتر باشد")
            .max(64, "رمز عبور جدید نمی‌تواند بیشتر از ۶۴ کاراکتر باشد"),
        confirmPassword: Yup.string()
            .required("تکرار رمز عبور جدید الزامی است")
            .oneOf(
                [Yup.ref("newPassword")],
                "تکرار رمز عبور با رمز جدید مطابقت ندارد"
            ),
    })
    .test(
        "new-not-same-as-current",
        "رمز عبور جدید نباید با رمز فعلی یکسان باشد",
        function (values: any) {
            if (!values.currentPassword || !values.newPassword) return true;
            if (values.currentPassword === values.newPassword) {
                return this.createError({
                    path: "newPassword",
                    message: "رمز عبور جدید نباید با رمز فعلی یکسان باشد",
                });
            }
            return true;
        }
    );

// ============================================================
// کامپوننت اصلی
// ============================================================
export default function ChangePassword() {
    const navigate = useNavigate();
    const { setAction } = useHeaderAction();
    const changePassword = useChangePassword();

    useEffect(() => {
        setAction(
            <HeaderSubPages
                title="تغییر رمز عبور"
                onBack={() => navigate(-1)}
            />
        );
        return () => setAction(null);
    }, [setAction, navigate]);

    const handleSubmit = (
        values: any,
        { setSubmitting, resetForm }: any
    ) => {
        changePassword.mutate(
            {
                oldPassword: values.currentPassword,
                newPassword: values.newPassword,
                repeatPass: values.confirmPassword,
            },
            {
                onSuccess: (res: any) => {
                    setSubmitting(false);
                    toastify("success", "رمز عبور با موفقیت تغییر کرد");
                    resetForm();
                    navigate(-1);
                },
             
            }
        );
    };

    return (
        <div className="flex flex-col pb-24">
            {/* ===== هدر داخلی ===== */}
            <div className="w-full flex justify-start items-center gap-2 border-b pb-3 mb-6">
                <div className="w-9 h-9 rounded-xl flex justify-center items-center bg-number-3">
                    <KeyRound size={18} className="text-brand-800" />
                </div>
                <h2 className="font-semibold text-neutral-700 text-sm">
                    تغییر رمز عبور
                </h2>
            </div>

            <Formik
                initialValues={{
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange
                validateOnBlur
            >
                {({ isSubmitting }) => {
                    const isPending =
                        isSubmitting || changePassword.isPending;

                    return (
                        <Form className="w-full flex flex-col gap-4">
                            {/* ===== رمز فعلی ===== */}
                            <Field name="currentPassword">
                                {({ field, meta }: FieldProps<string>) => (
                                    <PasswordInput
                                        field={field}
                                        meta={meta}
                                        label="رمز عبور فعلی"
                                        autoComplete="current-password"
                                    />
                                )}
                            </Field>

                            {/* ===== رمز جدید ===== */}
                            <Field name="newPassword">
                                {({ field, meta }: FieldProps<string>) => (
                                    <PasswordInput
                                        field={field}
                                        meta={meta}
                                        label="رمز عبور جدید"
                                        autoComplete="new-password"
                                    />
                                )}
                            </Field>

                            {/* ===== تکرار رمز جدید ===== */}
                            <Field name="confirmPassword">
                                {({ field, meta }: FieldProps<string>) => (
                                    <PasswordInput
                                        field={field}
                                        meta={meta}
                                        label="تکرار رمز عبور جدید"
                                        autoComplete="new-password"
                                    />
                                )}
                            </Field>

                            {/* ===== دکمه‌ها ===== */}
                            <div className="w-full flex justify-center items-center gap-4 mt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    color="sky"
                                    className="w-1/2 h-12 text-[15px]"
                                    rounded="xl"
                                    onClick={() => navigate(-1)}
                                    disabled={isPending}
                                >
                                    انصراف
                                </Button>
                                <Button
                                    type="submit"
                                    variant="solid"
                                    color="sky"
                                    className="w-1/2 h-12 text-[15px]"
                                    rounded="xl"
                                    loading={isPending}
                                    disabled={isPending}
                                >
                                    {isPending
                                        ? "در حال ذخیره..."
                                        : "ذخیره تغییرات"}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}